import { Connection, clusterApiUrl, PublicKey, Transaction, SendOptions, SignaturePubkeyPair, Cluster } from "@solana/web3.js";
import Bundlr from "@bundlr-network/client/build/web";
import { log } from "./logger";
import { InsufficientFund } from "./errors";

export const getBundlrNetworkApi = (cluster: Cluster): string => {
	switch (cluster) {
		case "mainnet-beta": {
			return "https://node1.bundlr.network";
		};
		default: {
			return "https://devnet.bundlr.network";
		};
	}
};


export const topUpAccOnBundlrNet = async (connection: Connection, bundlr: Bundlr, account: PublicKey, minimumAmt: number) => {
	const bundlrBalance = await bundlr.getLoadedBalance();
	log.info(`SOL balance on Bundlr is ${bundlrBalance}`);
	if (bundlrBalance.isGreaterThan(minimumAmt)) {
		log.info("Account has sufficient funds");
		return;
	}

	// Check if it has sufficient SOL in L1.
	const sols = await connection.getBalance(account);
	if (minimumAmt > sols) {
		throw new InsufficientFund(minimumAmt, sols);
	}
	// Top up the shortfall plus a bit more
	const topUpAmt = ((minimumAmt - bundlrBalance.toNumber()) * 1.1).toFixed(0);
	log.info(`Topping up ${topUpAmt} for account.`);
	await bundlr.fund(topUpAmt);
	return;
};

/**
 * Function to sign message.
 */
export type SignMessageFn = (message: Uint8Array) => Promise<Uint8Array>;

/**
 * Upload a file to Arweave via Bundlr network.
 * 
 * Account must possess sufficient SOL to cover cost of upload.
 *
 * @param {Connection} RPC connection to solana node
 * @param {PublicKey} A Solana public key
 * @param {SignMessageFn} A closure that generates a signature from a message.
 * @param {SendTxFn} A closure that sends transaction
 * @param {string} base64 encoded image string.
 * @returns {string} Arweave tx id
 */
export const uploadFileToArweaveViaBundlr = async (cluster: Cluster, account: PublicKey, signMsgFn: SignMessageFn, content: string): Promise<string> => {
	const provider = {
		publicKey: {
			toBuffer: () => account.toBuffer(),
			byteLength: 32
		},
		signMessage: async (message: Uint8Array): Promise<Uint8Array> => {
			const signature = await signMsgFn(message);
			return signature;
		},
		sendTransaction: async (tx: Transaction, c: Connection, opts: SendOptions): Promise<string> => {
			const connection = new Connection(clusterApiUrl(cluster));
			if (tx.signatures.filter(txSignature => txSignature.signature === null)) {
				const msgBuffer = tx.serializeMessage();
				const signature = await signMsgFn(msgBuffer);

				/// Inject signature
				let signaturePubKeyPair = tx.signatures.pop() as SignaturePubkeyPair;
				signaturePubKeyPair.signature = Buffer.from(signature);
				tx.signatures = [signaturePubKeyPair];
			}
			const serializedTx = tx.serialize({
				verifySignatures: true,
				requireAllSignatures: true
			});
			const txId = await connection.sendRawTransaction(serializedTx, opts);
			return txId;
		},
	};
	const rpcTarget = clusterApiUrl(cluster);
	const connection = new Connection(rpcTarget);
	const bundlr = new Bundlr(getBundlrNetworkApi(cluster), "solana", provider, { providerUrl: rpcTarget });
	// Find cost of upload
	const price = await bundlr.getPrice(content.length);

	// Lazy top up of account in Bundlr node if lacking funds
	await topUpAccOnBundlrNet(connection, bundlr, account, price.toNumber());

	// Create, sign, and upload transaction
	const tx = bundlr.createTransaction(content);
	await tx.sign();
	log.info(`Uploading ${tx.id} to BundlrNetwork...`);
	await tx.upload();
	return tx.id;
}

